import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/base/Button';

interface Plan {
  id: string;
  name: string;
  price: number;
  period: string;
  description: string;
  features: string[];
  recommended?: boolean;
  popular?: boolean;
  buttonText: string;
  buttonVariant: 'primary' | 'secondary' | 'outline';
}

const plans: Plan[] = [
  {
    id: 'free',
    name: 'Plan Gratuito',
    price: 0,
    period: 'Siempre gratis',
    description: 'Perfecto para comenzar tu viaje financiero',
    features: [
      'Hasta 10 transacciones por mes',
      'Dashboard básico',
      'Reportes mensuales',
      'Soporte por email',
      'Categorización manual',
      'Acceso móvil'
    ],
    buttonText: 'Comenzar Gratis',
    buttonVariant: 'outline'
  },
  {
    id: 'premium',
    name: 'Plan Premium',
    price: 9.99,
    period: 'por mes',
    description: 'Para usuarios que buscan más control',
    features: [
      'Transacciones ilimitadas',
      'Dashboard avanzado con gráficos',
      'Reportes semanales y personalizados',
      'Soporte prioritario',
      'IA para categorización automática',
      'Sincronización bancaria',
      'Alertas y notificaciones',
      'Exportar datos (PDF, Excel)',
      'Múltiples cuentas bancarias'
    ],
    recommended: true,
    buttonText: 'Elegir Premium',
    buttonVariant: 'primary'
  },
  {
    id: 'professional',
    name: 'Plan Profesional',
    price: 19.99,
    period: 'por mes',
    description: 'Para profesionales y pequeñas empresas',
    features: [
      'Todo lo del Plan Premium',
      'Gestión de múltiples usuarios',
      'Dashboard empresarial',
      'Reportes avanzados y analytics',
      'API de integración',
      'Soporte telefónico 24/7',
      'Consultoría financiera mensual',
      'Presupuestos y proyecciones',
      'Integración con contabilidad',
      'Backup automático'
    ],
    popular: true,
    buttonText: 'Elegir Profesional',
    buttonVariant: 'secondary'
  }
];

export default function Plans() {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');

  const handlePlanSelect = (planId: string) => {
    setSelectedPlan(planId);
    
    if (planId === 'free') {
      // Para plan gratuito, ir directo al registro
      navigate('/register?plan=free');
    } else {
      // Para planes pagos, ir a página de checkout
      navigate(`/checkout?plan=${planId}&billing=${billingPeriod}`);
    }
  };

  const getYearlyPrice = (monthlyPrice: number) => {
    return monthlyPrice * 10; // 2 meses gratis al año
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-emerald-900">
      {/* Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-emerald-400 rounded-full animate-pulse opacity-60"></div>
          <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-blue-400 rounded-full animate-ping opacity-40"></div>
          <div className="absolute bottom-1/4 left-1/3 w-3 h-3 bg-emerald-300 rounded-full animate-bounce opacity-30"></div>
          <div className="absolute top-2/3 right-1/4 w-2 h-2 bg-blue-300 rounded-full animate-pulse opacity-50"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Back button */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/')}
              className="text-white/80 hover:text-white transition-colors cursor-pointer flex items-center group"
            >
              <i className="ri-arrow-left-line mr-2 group-hover:-translate-x-1 transition-transform"></i>
              Volver al inicio
            </button>
          </div>

          {/* Header content */}
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-white mb-6">
              Elige tu <span className="text-emerald-400">Plan Perfecto</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Comienza gratis y escala cuando tu negocio crezca. Todos los planes incluyen 
              nuestra IA avanzada y soporte completo.
            </p>
          </div>

          {/* Billing toggle */}
          <div className="flex justify-center mb-12">
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-1 border border-white/20">
              <div className="flex">
                <button
                  onClick={() => setBillingPeriod('monthly')}
                  className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                    billingPeriod === 'monthly'
                      ? 'bg-emerald-600 text-white shadow-lg'
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  Mensual
                </button>
                <button
                  onClick={() => setBillingPeriod('yearly')}
                  className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 relative ${
                    billingPeriod === 'yearly'
                      ? 'bg-emerald-600 text-white shadow-lg'
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  Anual
                  <span className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs px-2 py-1 rounded-full">
                    -17%
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Plans grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-white/10 backdrop-blur-lg rounded-2xl p-8 border transition-all duration-300 hover:scale-105 hover:shadow-2xl ${
                plan.recommended
                  ? 'border-emerald-400 shadow-emerald-400/20 shadow-xl'
                  : 'border-white/20'
              } ${selectedPlan === plan.id ? 'ring-2 ring-emerald-400' : ''}`}
            >
              {/* Badges */}
              {plan.recommended && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-emerald-500 text-white px-4 py-2 rounded-full text-sm font-medium">
                    Recomendado
                  </span>
                </div>
              )}
              {plan.popular && (
                <div className="absolute -top-4 right-4">
                  <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                    Más Popular
                  </span>
                </div>
              )}

              {/* Plan header */}
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                <p className="text-gray-300 mb-6">{plan.description}</p>
                
                <div className="mb-6">
                  {plan.price === 0 ? (
                    <div>
                      <span className="text-4xl font-bold text-emerald-400">Gratis</span>
                      <p className="text-gray-400 mt-2">{plan.period}</p>
                    </div>
                  ) : (
                    <div>
                      <span className="text-4xl font-bold text-white">
                        ${billingPeriod === 'monthly' ? plan.price : getYearlyPrice(plan.price)}
                      </span>
                      <span className="text-gray-400 ml-2">
                        {billingPeriod === 'monthly' ? plan.period : 'por año'}
                      </span>
                      {billingPeriod === 'yearly' && plan.price > 0 && (
                        <p className="text-emerald-400 text-sm mt-1">
                          Ahorras ${(plan.price * 2).toFixed(2)} al año
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Features list */}
              <div className="mb-8">
                <ul className="space-y-3">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <i className="ri-check-line text-emerald-400 mr-3 mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA Button */}
              <Button
                variant={plan.buttonVariant}
                size="lg"
                onClick={() => handlePlanSelect(plan.id)}
                className={`w-full py-4 font-semibold transition-all duration-200 ${
                  plan.buttonVariant === 'primary'
                    ? 'bg-gradient-to-r from-emerald-600 to-blue-600 hover:from-emerald-700 hover:to-blue-700 transform hover:scale-105'
                    : plan.buttonVariant === 'secondary'
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transform hover:scale-105'
                    : 'border-2 border-emerald-400 text-emerald-400 hover:bg-emerald-400 hover:text-white transform hover:scale-105'
                }`}
              >
                <i className={`mr-2 ${
                  plan.id === 'free' ? 'ri-gift-line' : 
                  plan.id === 'premium' ? 'ri-vip-crown-line' : 
                  'ri-building-line'
                }`}></i>
                {plan.buttonText}
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-8 border border-white/10">
          <h2 className="text-3xl font-bold text-white text-center mb-8">
            Preguntas Frecuentes
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                ¿Puedo cambiar de plan en cualquier momento?
              </h3>
              <p className="text-gray-300">
                Sí, puedes actualizar o degradar tu plan cuando gustes. Los cambios se aplicarán inmediatamente.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                ¿El plan gratuito tiene limitaciones de tiempo?
              </h3>
              <p className="text-gray-300">
                No, el plan gratuito es permanente con las funcionalidades básicas incluidas.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                ¿Ofrecen reembolsos?
              </h3>
              <p className="text-gray-300">
                Sí, ofrecemos garantía de devolución del 100% durante los primeros 14 días.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                ¿Mis datos están seguros?
              </h3>
              <p className="text-gray-300">
                Absolutamente. Utilizamos encriptación de nivel bancario y cumplimos con todas las regulaciones.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Trust indicators */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="text-center">
          <p className="text-gray-400 mb-8">Confiado por más de 10,000 usuarios en todo el mundo</p>
          <div className="flex justify-center items-center space-x-8 opacity-60">
            <div className="text-white/60">
              <i className="ri-shield-check-line text-2xl"></i>
              <p className="text-sm mt-2">Seguro SSL</p>
            </div>
            <div className="text-white/60">
              <i className="ri-bank-line text-2xl"></i>
              <p className="text-sm mt-2">Nivel Bancario</p>
            </div>
            <div className="text-white/60">
              <i className="ri-customer-service-line text-2xl"></i>
              <p className="text-sm mt-2">Soporte 24/7</p>
            </div>
            <div className="text-white/60">
              <i className="ri-medal-line text-2xl"></i>
              <p className="text-sm mt-2">Certificado</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}